import { api } from './api';

export interface BurialPlotSearchData {
  burialArea: string;
  burialSize: 'ADULT' | 'CHILD';
  urgencyLevel: 'URGENT' | 'STANDARD';
}

export interface BurialPlotProvider {
  provider: {
    id: string;
    companyName: string;
    phone?: string;
    coordinates?: any;
  };
  plot: {
    id: string;
    plotNumber: string;
    section: string;
    row: string;
    plotSize: string;
    dimensions?: string;
  };
  pricing: {
    basePrice: number;
    urgencySurcharge: number;
    total: number;
    breakdown: string;
    currency: string;
  };
}

export interface BurialPlotBookingData {
  providerId: string;
  caseId?: string;
  burialSize: 'ADULT' | 'CHILD';
  burialArea: string;
  preparationDate?: Date;
  urgencyLevel: 'URGENT' | 'STANDARD';
  specialRequests?: string;
  scheduledDateTime?: Date;
  areaCoordinates?: { lat: number; lng: number };
  plotId?: string;
}

export interface BankTransferPaymentData {
  amount: number;
  bankName: string;
  accountHolder: string;
  accountNumber: string;
  receiptUrl?: string;
  notes?: string;
}

export interface BurialPlotBookingResponse {
  success: boolean;
  data?: any;
  message?: string;
  booking?: {
    id: string;
    status: string;
    transferReference: string;
    pricing: {
      total: number;
      currency: string;
      breakdown: string;
    };
    provider: {
      id: string;
      companyName: string;
      phone: string;
    };
    plot: {
      id: string;
      plotNumber: string;
      section: string;
      row: string;
      plotSize: string;
      dimensions: string;
    };
    scheduledDateTime: Date;
    urgencyLevel: string;
    burialSize: string;
  };
  paymentStatus?: string;
}

export interface PricingQuote {
  basePrice: number;
  urgencySurcharge: number;
  total: number;
  breakdown: string;
  currency: string;
}

class BurialPlotBookingService {
  async searchBurialPlots(searchData: BurialPlotSearchData): Promise<BurialPlotProvider[]> {
    try {
      const response = await api.post('/burial-plot-booking/search', searchData);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to search burial plots');
      }
    } catch (error) {
      console.error('Search burial plots error:', error);
      throw error;
    }
  }

  async getPricingQuote(burialSize: string, urgencyLevel: string): Promise<PricingQuote> {
    try {
      const response = await api.post('/burial-plot-booking/pricing-quote', {
        burialSize,
        urgencyLevel,
      });
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to get pricing quote');
      }
    } catch (error) {
      console.error('Pricing quote error:', error);
      throw error;
    }
  }

  async createBooking(bookingData: BurialPlotBookingData): Promise<BurialPlotBookingResponse> {
    try {
      const response = await api.post('/burial-plot-booking/book', bookingData);
      return response.data;
    } catch (error: any) {
      console.error('Create booking error - detailed:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);

      if (error.message && error.message.includes('Cannot read properties of undefined')) {
        console.error('The error is likely in the API call or response handling');
      }

      throw error;
    }
  }

  async getBookingDetails(bookingId: string): Promise<any> {
    try {
      const response = await api.get(`/burial-plot-booking/${bookingId}`);
      return response.data; // Return the full response with success/data structure
    } catch (error) {
      console.error('Get booking details error:', error);
      throw error;
    }
  }

  async updateBookingStatus(bookingId: string, status: string): Promise<any> {
    try {
      const response = await api.patch(`/burial-plot-booking/${bookingId}/status`, {
        status,
      });
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update booking status');
      }
    } catch (error) {
      console.error('Update booking status error:', error);
      throw error;
    }
  }

  async cancelBooking(bookingId: string, reason?: string): Promise<any> {
    try {
      const response = await api.delete(`/burial-plot-booking/${bookingId}`, {
        data: { reason },
      });
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to cancel booking');
      }
    } catch (error) {
      console.error('Cancel booking error:', error);
      throw error;
    }
  }

  async deleteBooking(bookingId: string): Promise<any> {
    try {
      const response = await api.delete(`/burial-plot-booking/${bookingId}/delete`);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to delete booking');
      }
    } catch (error) {
      console.error('Delete booking error:', error);
      throw error;
    }
  }

  async getPlotSizePricing(): Promise<any[]> {
    try {
      const response = await api.get('/burial-plot-booking/sizes/pricing');
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to get plot size pricing');
      }
    } catch (error) {
      console.error('Get plot size pricing error:', error);
      throw error;
    }
  }

  async getUrgencySurcharges(): Promise<any[]> {
    try {
      const response = await api.get('/burial-plot-booking/urgency/surcharges');
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to get urgency surcharges');
      }
    } catch (error) {
      console.error('Get urgency surcharges error:', error);
      throw error;
    }
  }

  async getUserBookings(): Promise<any[]> {
    try {
      const response = await api.get('/burial-plot-booking/my-bookings');
      if (response.data.success) {
        return response.data.data;
      } else {
        // Handle "Booking not found" as empty array instead of error
        if (response.data.message === 'Booking not found') {
          return [];
        }
        throw new Error(response.data.message || 'Failed to get user bookings');
      }
    } catch (error) {
      console.error('Get user bookings error:', error);
      // Also handle network errors that might contain "Booking not found"
      if (error.response?.data?.message === 'Booking not found') {
        return [];
      }
      throw error;
    }
  }

  async getCemeterySections(assetId: string, plotSize?: 'ADULT' | 'CHILD'): Promise<any[]> {
    try {
      const url = plotSize
        ? `/cemeteries/${assetId}/sections?plotSize=${plotSize}`
        : `/cemeteries/${assetId}/sections`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Get cemetery sections error:', error);
      throw error;
    }
  }

  async getSectionPlots(assetId: string, sectionName: string, plotSize?: 'ADULT' | 'CHILD'): Promise<any> {
    try {
      const url = plotSize
        ? `/cemeteries/${assetId}/sections/${encodeURIComponent(sectionName)}/plots?plotSize=${plotSize}`
        : `/cemeteries/${assetId}/sections/${encodeURIComponent(sectionName)}/plots`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Get section plots error:', error);
      throw error;
    }
  }
}

export const burialPlotBookingService = new BurialPlotBookingService();